import { useSearchParams } from "react-router-dom";

import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { logger } from "src/utils/logger";

export type StepId =
  | "define-version-settings"
  | "define-case-properties"
  | "configure-file-categories"
  | "select-decision-flow"
  | "configure-case-actions"
  | "define-tasks"
  | "define-workflow-rules"
  | "configure-case-blocks"
  | "configure-copilot";

export type Step = {
  id: StepId;
  title: string;
  description: string;
  docLink?: string;
};

export const ALL_STEPS: Step[] = [
  {
    id: "define-version-settings",
    title: "Version name",
    description: "Provide basic details for the template",
  },
  {
    id: "define-case-properties",
    title: "Properties",
    description: "Define the properties of cases created using this template",
  },
  {
    id: "configure-case-blocks",
    title: "Case blocks",
    description:
      "Configure case blocks, select data source, adjust size, reorder, and preview the case layout",
  },
  {
    id: "configure-file-categories",
    title: "File categories",
    description:
      "Define file categories to organize documents associated with a case",
  },
  {
    id: "define-tasks",
    title: "Tasks",
    description:
      "Define all tasks reviewers should complete while resolving a case to guide them through the process",
  },
  {
    id: "configure-case-actions",
    title: "Actions",
    description: "Define all actions reviewers can perform on a case",
  },
  {
    id: "define-workflow-rules",
    title: "Workflow rules",
    description:
      "Define actions, their required tasks, and triggered automations available in each status of the case workflow",
  },
  {
    id: "configure-copilot",
    title: "Copilot",
    description:
      "Configure AI assistance to accelerate the review process. When enabled, the Copilot chat will be available on all case pages using this template.",
    // docLink: "https://docs.taktile.com/copilot",
  },
  {
    id: "select-decision-flow",
    title: "Preparation flow",
    description:
      "Optionally select a Decision Flow to automatically run on case creation to prepare a case for review, e.g., by adding an AI summary",
  },
] as const;

export const useSteps = (): Step[] => {
  const { cmCopilotEnabled } = useWorkspaceFeatureGates();

  if (!cmCopilotEnabled) {
    return ALL_STEPS.filter((step) => step.id !== "configure-copilot");
  }

  return ALL_STEPS;
};

export const getStepById = (stepId: StepId): Step => {
  const step = ALL_STEPS.find((step) => step.id === stepId);

  if (!step) {
    logger.error(`Step with id "${stepId}" not found`);
  }

  return step ?? ALL_STEPS[0];
};

export const useCurrentStep = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const steps = useSteps();

  const rawStepId = searchParams.get("step");
  const isValidStep = rawStepId && steps.some((step) => step.id === rawStepId);

  if (rawStepId && !isValidStep) {
    logger.warn(
      `Invalid or inaccessible step ID "${rawStepId}" in URL. Falling back to default step.`,
    );
  }

  const currentStepId = isValidStep ? rawStepId : steps[0].id;

  const setCurrentStep = (stepId: StepId) => {
    const stepExists = steps.some((step) => step.id === stepId);
    if (!stepExists) {
      logger.error(
        `Attempted to navigate to step "${stepId}" which is not in the available steps.`,
      );
      return;
    }

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("step", stepId);
      return newParams;
    });
  };

  return [currentStepId as StepId, setCurrentStep] as const;
};
